import imageCompression from 'browser-image-compression';
import { logger } from '@/utils/logger';
import { snapshotPickedFile } from '@/utils/pickedFile';

/**
 * Ceiling applied to the *result* of compression. A normal camera photo lands
 * around 1MB and never approaches this.
 */
export const MAX_UPLOAD_BYTES = 15 * 1024 * 1024;

/** Same targets the chat image upload has shipped with. */
const DEFAULT_OPTIONS = {
  maxSizeMB: 1,
  maxWidthOrHeight: 1920,
};

/**
 * Formats we hand back untouched. Anything else (HEIC/HEIF from a phone
 * camera, or an unknown type) is re-encoded to JPEG. PNG is deliberately
 * preserved: organization avatars use this path and forcing JPEG would flatten
 * transparency onto black.
 */
const PASSTHROUGH_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const EXTENSION_BY_TYPE: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
};

export type CompressFailureReason = 'empty' | 'unreadable' | 'undecodable' | 'too-large';

export type CompressImageResult =
  | { ok: true; file: File }
  | { ok: false; reason: CompressFailureReason; size: number };

/** Replaces the extension, tolerating names with no extension at all. */
function withExtension(name: string, ext: string): string {
  const base = name.replace(/\.[^./\\]+$/, '').trim() || 'image';
  return `${base}.${ext}`;
}

/**
 * `browser-image-compression` rejects with a DOM `Event` when the image fails
 * to decode, and `String(event)` is just "[object Event]". Pull out something
 * that actually identifies the failure.
 */
function describeError(err: unknown): string {
  if (err instanceof Error) return `${err.name}: ${err.message}`;
  if (typeof Event !== 'undefined' && err instanceof Event) {
    const target = err.target as { error?: unknown; src?: string } | null;
    return `${err.type} event${target?.error ? ` (${String(target.error)})` : ''}`;
  }
  return String(err);
}

/**
 * Downscale an image chosen from the device before upload.
 *
 * The returned File's name and `type` always agree with its bytes, because
 * `uploadProfileImages` derives both the storage extension and the upload
 * contentType from them — a JPEG named `.heic` would be stored mislabelled.
 *
 * A file we cannot decode is reported as such rather than passed through: the
 * browser could not render it either, so uploading it would produce a broken
 * avatar (and, when large, an opaque network failure on upload).
 */
export async function compressImageFile(
  file: File,
  options: Partial<typeof DEFAULT_OPTIONS> = {},
): Promise<CompressImageResult> {
  // A picker can hand back a placeholder it never managed to read (a cloud
  // item, a revoked content:// URI). Nothing downstream can use those bytes.
  if (file.size === 0) {
    logger.error('Picked image is empty', { name: file.name, type: file.type });
    return { ok: false, reason: 'empty', size: 0 };
  }

  const outputType = PASSTHROUGH_TYPES.includes(file.type) ? file.type : 'image/jpeg';

  // Read the OS file reference once, before the library touches it repeatedly.
  const source = await snapshotPickedFile(file);
  if (!source) {
    return { ok: false, reason: 'unreadable', size: file.size };
  }

  // The worker path is dramatically faster, but it is also the more fragile of
  // the two; retry on the main thread before declaring the file undecodable.
  for (const useWebWorker of [true, false]) {
    try {
      const compressed = await imageCompression(source, {
        ...DEFAULT_OPTIONS,
        ...options,
        useWebWorker,
        fileType: outputType,
      });
      const result = new File(
        [compressed],
        withExtension(file.name, EXTENSION_BY_TYPE[outputType] ?? 'jpg'),
        { type: outputType },
      );
      if (result.size > MAX_UPLOAD_BYTES) {
        return { ok: false, reason: 'too-large', size: result.size };
      }
      return { ok: true, file: result };
    } catch (err) {
      logger.error('Image compression failed', {
        useWebWorker,
        name: file.name,
        type: file.type || '(none)',
        sizeKB: Math.round(file.size / 1024),
        error: describeError(err),
      });
    }
  }

  return { ok: false, reason: 'undecodable', size: file.size };
}

/** User-facing copy for a failed pick. */
export function compressFailureMessage(reason: CompressFailureReason): string {
  switch (reason) {
    case 'empty':
      return 'That image could not be read. Try picking it again, or choose another photo.';
    case 'unreadable':
      return 'Could not read that photo. Please pick it again.';
    case 'too-large':
      return 'That image is too large to upload. Try another photo.';
    default:
      return 'That image format is not supported. Try a JPEG or PNG.';
  }
}
